/* eslint-disable max-lines */
/**
 * Temporal Evolution Visualization
 * Shows relationship changes over time with timeline and trend analysis
 */

import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Collapse,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  SkipNext as NextIcon,
  SkipPrevious as PrevIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import useTokenExaminerStore from '../stores/tokenExaminerStore';
import {
  getFaanaHealthColor,
} from '../utils/faanaScoring';
import RelationshipDetailPanel from '../components/RelationshipDetailPanel';

const TIME_RANGES = [
  { label: '1 Week', days: 7 },
  { label: '1 Month', days: 30 },
  { label: '3 Months', days: 90 },
  { label: '6 Months', days: 180 },
  { label: '1 Year', days: 365 },
  { label: 'All Time', days: null },
];

const ANIMATION_SPEEDS = [
  { label: 'Slow', speed: 2000 },
  { label: 'Normal', speed: 1000 },
  { label: 'Fast', speed: 500 },
];

// eslint-disable-next-line max-lines-per-function
function TemporalEvolution() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [selectedTimeRange, setSelectedTimeRange] = useState(TIME_RANGES[2]); // 3 months
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeIndex, setCurrentTimeIndex] = useState(0);
  const [animationSpeed, setAnimationSpeed] = useState(ANIMATION_SPEEDS[1]);
  const [showTrendLines, setShowTrendLines] = useState(true);
  const [groupByDimension, setGroupByDimension] = useState('overall');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [controlsExpanded, setControlsExpanded] = useState(true);
  const zoomBehaviorRef = useRef(null);
  const animationIntervalRef = useRef(null);

  const {
    _relationships,
    _entities,
    _temporalData,
    selectedEntity,
    getFilteredRelationships,
    _fetchTemporalData,
  } = useTokenExaminerStore();

  const filteredRelationships = getFilteredRelationships();

  // Get temporal data for visualization
  const [timelineData, setTimelineData] = useState([]);
  const [playbackData, setPlaybackData] = useState([]);

  useEffect(() => {
    if (selectedEntity && filteredRelationships.length > 0) {
      const timeoutId = setTimeout(() => {
        generateTimelineData();
      }, 300); // Debounce to avoid rapid updates

      return () => clearTimeout(timeoutId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity, filteredRelationships, selectedTimeRange, groupByDimension]);

  const generateTimelineData = () => {
    // TODO: When Token Examiner backend supports temporal data, replace with:
    // const temporalData = await api.getRelationshipTemporalData(entityId, { range: selectedTimeRange });

    // Generate synthetic temporal data for demonstration
    // This simulates how relationship health evolves over time
    const now = new Date();
    const startDate = selectedTimeRange.days
      ? new Date(now.getTime() - selectedTimeRange.days * 24 * 60 * 60 * 1000)
      : new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

    const timePoints = [];
    const dayInterval = Math.max(1, Math.floor((selectedTimeRange.days || 365) / 50));

    for (let d = new Date(startDate); d <= now; d.setDate(d.getDate() + dayInterval)) {
      timePoints.push(new Date(d));
    }

    const data = filteredRelationships.map(relationship => {
      const points = timePoints.map((date, index) => {
        // Simulate health evolution with some randomness and trends
        const baseHealth = relationship.overallHealth || 50;
        const timeProgress = index / timePoints.length;
        const trend = relationship.trajectoryPrediction === 'strengthening' ? 10 :
                     relationship.trajectoryPrediction === 'weakening' ? -10 : 0;

        const noise = (Math.random() - 0.5) * 10;
        const trendEffect = trend * timeProgress;
        const cyclical = Math.sin(timeProgress * Math.PI * 2) * 5;

        let health = Math.max(0, Math.min(100, baseHealth + trendEffect + noise + cyclical));

        // Generate dimension-specific data if needed
        const dimensions = {};
        if (groupByDimension !== 'overall') {
          ['trust', 'communication', 'psychologicalSafety', 'collaboration'].forEach(dim => {
            dimensions[dim] = Math.max(0, Math.min(10,
              (health / 10) + (Math.random() - 0.5) * 2
            ));
          });
        }

        return {
          date,
          health,
          dimensions,
          events: index % 10 === 0 ? ['Meeting', 'Email', 'Project Update'][Math.floor(Math.random() * 3)] : null,
        };
      });

      return {
        relationshipId: relationship.id,
        relationshipType: relationship.relationshipType,
        sourceEntity: relationship.sourceEntity,
        targetEntity: relationship.targetEntity,
        points,
        relationship,
      };
    });

    setTimelineData(data);
    setPlaybackData(timePoints.map((date, index) => ({
      date,
      index,
      relationships: data.map(rel => ({
        ...rel.relationship,
        currentHealth: rel.points[index]?.health || 0,
        currentDimensions: rel.points[index]?.dimensions || {},
        events: rel.points[index]?.events,
      }))
    })));
  };

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Animation playback - user controlled
  useEffect(() => {
    if (isPlaying && playbackData.length > 0) {
      animationIntervalRef.current = setInterval(() => {
        setCurrentTimeIndex(prev => {
          if (prev >= playbackData.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, animationSpeed.speed);
    } else {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    }

    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    };
  }, [isPlaying, animationSpeed.speed, playbackData.length]);

  // Handle timeline scrubbing
  const handleTimelineSeek = (event, newValue) => {
    setCurrentTimeIndex(newValue);
    setIsPlaying(false); // Pause when user manually seeks
  };

  // Render timeline visualization
  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    if (!dimensions.width || !dimensions.height || timelineData.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 40, right: 40, bottom: 60, left: 60 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    svg.attr('width', dimensions.width).attr('height', dimensions.height);

    // Create a group for zooming
    const zoomGroup = svg.append('g');

    // Initialize zoom behavior only once
    if (!zoomBehaviorRef.current) {
      zoomBehaviorRef.current = d3.zoom()
        .scaleExtent([0.5, 5])
        .on('zoom', (event) => {
          zoomGroup.attr('transform', event.transform);
          setZoomLevel(event.transform.k);
        });
    }

    // Apply zoom behavior to SVG
    svg.call(zoomBehaviorRef.current);

    const container = zoomGroup.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Scales
    const timeExtent = d3.extent(timelineData[0]?.points || [], d => d.date);
    const xScale = d3.scaleTime()
      .domain(timeExtent)
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([0, 100])
      .range([height, 0]);

    // Add axes
    const xAxis = d3.axisBottom(xScale)
      .ticks(6)
      .tickFormat(d3.timeFormat('%b %d'));

    const yAxis = d3.axisLeft(yScale)
      .ticks(5);

    container.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis);

    container.append('g')
      .attr('class', 'y-axis')
      .call(yAxis);

    // Add axis labels
    container.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left)
      .attr('x', 0 - (height / 2))
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .text('Health Score');

    container.append('text')
      .attr('transform', `translate(${width / 2}, ${height + margin.bottom - 10})`)
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .text('Time');

    // Line generator
    const line = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(groupByDimension === 'overall' ? d.health : (d.dimensions[groupByDimension] * 10) || 0))
      .curve(d3.curveMonotoneX);

    // Add relationship lines
    const relationshipGroups = container.selectAll('.relationship-line')
      .data(timelineData)
      .enter().append('g')
      .attr('class', 'relationship-line');

    // Add trend lines if enabled
    if (showTrendLines) {
      relationshipGroups.append('path')
        .datum(d => d.points)
        .attr('fill', 'none')
        .attr('stroke', function(points) {
          // Calculate mean health safely
          if (!points || points.length === 0) return '#9E9E9E';
          const healthValues = points.map(p => p.health || 0);
          const meanHealth = healthValues.reduce((sum, val) => sum + val, 0) / healthValues.length;
          return getFaanaHealthColor(meanHealth);
        })
        .attr('stroke-width', 2)
        .attr('stroke-opacity', 0.7)
        .attr('d', line);
    }

    // Add data points with hover effects
    relationshipGroups.selectAll('.data-point')
      .data(d => d.points.map(p => ({ ...p, relationship: d.relationship })))
      .enter().append('circle')
      .attr('class', 'data-point')
      .attr('cx', d => xScale(d.date))
      .attr('cy', d => yScale(groupByDimension === 'overall' ? d.health : (d.dimensions[groupByDimension] * 10) || 0))
      .attr('r', 4)
      .attr('fill', d => getFaanaHealthColor(groupByDimension === 'overall' ? d.health : (d.dimensions[groupByDimension] * 10) || 0))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .style('transition', 'all 0.2s ease')
      .on('mouseenter', function(event, d) {
        d3.select(this)
          .attr('r', 6)
          .attr('stroke-width', 2);
        handlePointMouseEnter(event, d);
      })
      .on('mouseleave', function() {
        d3.select(this)
          .attr('r', 4)
          .attr('stroke-width', 1);
        handlePointMouseLeave();
      })
      .on('click', handlePointClick);

    // Add playback timeline indicator
    const playbackIndicator = container.append('line')
      .attr('class', 'playback-indicator')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#2196F3')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')
      .style('opacity', 0);

    // Update playback indicator position
    if (playbackData.length > 0 && currentTimeIndex < playbackData.length) {
      const currentDate = playbackData[currentTimeIndex].date;
      playbackIndicator
        .attr('x1', xScale(currentDate))
        .attr('x2', xScale(currentDate))
        .style('opacity', 1);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions, timelineData, showTrendLines, groupByDimension, currentTimeIndex, zoomLevel]);

  // Event handlers
  const handlePointMouseEnter = (event, d) => {
    setHoveredPoint(d);
  };

  const handlePointMouseLeave = () => {
    setHoveredPoint(null);
  };

  const handlePointClick = (event, d) => {
    setSelectedRelationship(d.relationship);
  };

  const handlePlayPause = () => {
    if (!isPlaying && currentTimeIndex >= playbackData.length - 1) {
      // Reset to beginning if at the end
      setCurrentTimeIndex(0);
    }
    setIsPlaying(!isPlaying);
  };

  const handlePrevious = () => {
    setCurrentTimeIndex(Math.max(0, currentTimeIndex - 1));
  };

  const handleNext = () => {
    setCurrentTimeIndex(Math.min(playbackData.length - 1, currentTimeIndex + 1));
  };

  const currentPlaybackData = playbackData[currentTimeIndex];

  // Zoom handlers
  const handleZoomIn = () => {
    if (zoomBehaviorRef.current && svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().duration(300).call(zoomBehaviorRef.current.scaleBy, 1.2);
    }
  };

  const handleZoomOut = () => {
    if (zoomBehaviorRef.current && svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().duration(300).call(zoomBehaviorRef.current.scaleBy, 0.8);
    }
  };

  const handleResetZoom = () => {
    if (zoomBehaviorRef.current && svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().duration(300).call(
        zoomBehaviorRef.current.transform,
        d3.zoomIdentity
      );
    }
  };

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Controls Panel */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1000,
          maxWidth: 320,
        }}
        elevation={3}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            pb: controlsExpanded ? 0 : 2,
            cursor: 'pointer',
          }}
          onClick={() => setControlsExpanded(!controlsExpanded)}
        >
          <Typography variant="subtitle1">
            Temporal Controls
          </Typography>
          <IconButton size="small">
            {controlsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={controlsExpanded}>
          <Box sx={{ px: 2, pb: 2 }}>
            {/* Time Range */}
            <FormControl fullWidth sx={{ mb: 2 }} size="small">
          <InputLabel>Time Range</InputLabel>
          <Select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            label="Time Range"
          >
            {TIME_RANGES.map(range => (
              <MenuItem key={range.label} value={range}>
                {range.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Dimension Selection */}
        <FormControl fullWidth sx={{ mb: 2 }} size="small">
          <InputLabel>Group By</InputLabel>
          <Select
            value={groupByDimension}
            onChange={(e) => setGroupByDimension(e.target.value)}
            label="Group By"
          >
            <MenuItem value="overall">Overall Health</MenuItem>
            <MenuItem value="trust">Trust</MenuItem>
            <MenuItem value="communication">Communication</MenuItem>
            <MenuItem value="psychologicalSafety">Psychological Safety</MenuItem>
            <MenuItem value="collaboration">Collaboration</MenuItem>
          </Select>
        </FormControl>

        {/* Display Options */}
        <FormControlLabel
          control={
            <Switch
              checked={showTrendLines}
              onChange={(e) => setShowTrendLines(e.target.checked)}
            />
          }
          label="Show Trend Lines"
          sx={{ mb: 1 }}
        />

        <Divider sx={{ my: 2 }} />

        {/* Playback Controls */}
        <Typography variant="subtitle2" gutterBottom>
          Playback
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={handlePrevious} disabled={currentTimeIndex === 0}>
            <PrevIcon />
          </IconButton>
          <IconButton onClick={handlePlayPause}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </IconButton>
          <IconButton
            onClick={handleNext}
            disabled={currentTimeIndex >= playbackData.length - 1}
          >
            <NextIcon />
          </IconButton>
        </Box>

        {/* Timeline Scrubber */}
        {playbackData.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Timeline ({currentTimeIndex + 1} of {playbackData.length})
            </Typography>
            <Slider
              value={currentTimeIndex}
              min={0}
              max={Math.max(0, playbackData.length - 1)}
              step={1}
              onChange={handleTimelineSeek}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) =>
                playbackData[value]?.date?.toLocaleDateString() || ''
              }
              marks={playbackData.length < 20 ? playbackData.map((_, idx) => ({
                value: idx,
                label: idx % 5 === 0 ? (playbackData[idx]?.date?.toLocaleDateString().split('/')[0] || '') : ''
              })) : false}
              sx={{
                '& .MuiSlider-thumb': {
                  width: 16,
                  height: 16,
                },
                '& .MuiSlider-track': {
                  height: 4,
                },
                '& .MuiSlider-rail': {
                  height: 4,
                  opacity: 0.3,
                },
              }}
            />
          </Box>
        )}

        {/* Animation Speed */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Speed</InputLabel>
          <Select
            value={animationSpeed}
            onChange={(e) => setAnimationSpeed(e.target.value)}
            label="Speed"
          >
            {ANIMATION_SPEEDS.map(speed => (
              <MenuItem key={speed.label} value={speed}>
                {speed.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Zoom Controls */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Zoom: {Math.round(zoomLevel * 100)}%
          </Typography>
          <ButtonGroup size="small" fullWidth>
            <Button onClick={handleZoomOut} startIcon={<ZoomOutIcon />}>
              Out
            </Button>
            <Button onClick={handleResetZoom}>
              Reset
            </Button>
            <Button onClick={handleZoomIn} startIcon={<ZoomInIcon />}>
              In
            </Button>
          </ButtonGroup>
        </Box>

        {/* Current Time */}
        {currentPlaybackData && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>Current:</strong> {currentPlaybackData.date.toLocaleDateString()}
            </Typography>
          </Alert>
        )}
          </Box>
        </Collapse>
      </Paper>

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          p: 2,
          zIndex: 1000,
          maxWidth: 200,
        }}
        elevation={3}
      >
        <Typography variant="subtitle2" gutterBottom>
          Health Trends
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon sx={{ color: '#4CAF50' }} />
            <Typography variant="caption">Improving</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingFlatIcon sx={{ color: '#FF9800' }} />
            <Typography variant="caption">Stable</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingDownIcon sx={{ color: '#F44336' }} />
            <Typography variant="caption">Declining</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Current Snapshot */}
      {currentPlaybackData && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            p: 2,
            zIndex: 1000,
            maxWidth: 400,
          }}
          elevation={3}
        >
          <Typography variant="subtitle2" gutterBottom>
            Snapshot: {currentPlaybackData.date.toLocaleDateString()}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {currentPlaybackData.relationships.slice(0, 5).map((rel, idx) => (
              <Chip
                key={idx}
                label={`${rel.relationshipType}: ${Math.round(rel.currentHealth)}`}
                size="small"
                sx={{
                  backgroundColor: getFaanaHealthColor(rel.currentHealth),
                  color: 'white',
                }}
              />
            ))}
            {currentPlaybackData.relationships.length > 5 && (
              <Chip
                label={`+${currentPlaybackData.relationships.length - 5} more`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Paper>
      )}

      {/* Hovered Point Info */}
      {hoveredPoint && (
        <Paper
          sx={{
            position: 'absolute',
            top: 200,
            right: 16,
            p: 2,
            zIndex: 1001,
            maxWidth: 250,
          }}
          elevation={8}
        >
          <Typography variant="subtitle2" gutterBottom>
            {hoveredPoint.relationship.relationshipType}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {hoveredPoint.date.toLocaleDateString()}
          </Typography>
          <Chip
            label={`Health: ${Math.round(hoveredPoint.health)}`}
            size="small"
            sx={{
              backgroundColor: getFaanaHealthColor(hoveredPoint.health),
              color: 'white',
            }}
          />
          {hoveredPoint.events && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Event: {hoveredPoint.events}
            </Typography>
          )}
        </Paper>
      )}

      {/* SVG Container */}
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />

      {/* Relationship Detail Panel */}
      {selectedRelationship && (
        <RelationshipDetailPanel
          relationship={selectedRelationship}
          onClose={() => setSelectedRelationship(null)}
        />
      )}
    </Box>
  );
}

export default React.memo(TemporalEvolution);